import React, {Suspense} from 'react'
import {Provider} from 'react-redux'
import {ThemeProvider} from 'emotion-theming'
import {CacheProvider} from '@emotion/core'
import {Router} from 'react-router-dom'

import Settings from 'Settings'
import Routes from './routes'
import InitializeSentry from 'components/InitializeSentry'
import InitializeABTesting from 'components/InitializeABTesting'

import CssBaseline from '@daedalus/atlas/src/components/designSystem/helpers/CssBaseline'
import UnknownErrorBoundary from 'components/UnknownErrorBoundary'
import ErrorOverlayMonitor from 'components/errorMonitors/ErrorOverlayMonitor'
import NetworkMonitor from 'components/errorMonitors/NetworkMonitor'
import useParams from 'components/data/URLParams/useParams'
import GetTheme from 'components/data/GetTheme'

import {CookieProCustomBanner} from '@daedalus/atlas/src/components/shared/cookies/CookieProCustomBanner'
import ConfigureI18n from '@daedalus/core/src/localization/components/ConfigureI18n'
import InjectMetaDescription from '@daedalus/core/src/localization/components/InjectMetaDescription'
import {DeviceLayoutProvider} from '@daedalus/atlas/src/context/deviceLayout'
import GridGlobalStyles from '@daedalus/atlas/src/components/designSystem/foundation/GridGlobalStyles'
import FontsLoader from '@daedalus/atlas/src/components/designSystem/helpers/FontsLoader'
import {evaluateInRuntime} from '@daedalus/core/src/utils/css/emotion-rtl'
import InitializeDataDogClient from '@daedalus/core/src/analytics/components/DataDogClient'
import LanguageConfig from '@daedalus/core/src/localization/components/LanguageConfig'
import {getTextDirection} from '@daedalus/core/src/localization/services/locale'
import OpticksProvider from '@daedalus/core/src/experiments/components/OpticksProvider'
import PasswordlessLogin from '@daedalus/atlas/src/components/shared/auth/PasswordlessLogin'
import TrackScreenshot from '@daedalus/core/src/analytics/components/TrackScreenshot'
import {FetchUser} from '@daedalus/core/src/auth/components/FetchUser'
import {SyncStateWithEnabledBrands} from '@daedalus/core/src/auth/components/SyncStateWithEnabledBrands'

import brand from 'configs/brand'

import initializeStore from './store'
import {history} from 'utils/history'

const store = initializeStore()

const App = () => {
  const urlParams = useParams()
  const {locale, userCountry, anonymousId} = urlParams
  const {isRtl} = getTextDirection(locale)

  const reCaptchaKey = Settings.get('REACT_APP_RECAPTCHA_KEY')

  return (
    <Provider store={store}>
      <InitializeDataDogClient
        clientToken={Settings.get('REACT_APP_DD_CLIENT_TOKEN')}
        applicationId={Settings.get('REACT_APP_DD_RUM_APP_ID')}
        appVersion={Settings.get('REACT_APP_VERSION')}
        appEnv={Settings.get('REACT_APP_ENV')}
        sampleRate={Number(Settings.get('REACT_APP_DD_SAMPLE_RATE'))}
        premiumSampleRate={Number(
          Settings.get('REACT_APP_DD_REPLAY_SAMPLE_RATE')
        )}
        service="checkout"
      />
      <TrackScreenshot />
      <Suspense fallback="">
        <OpticksProvider
          dataFileUrl={Settings.get('REACT_APP_OPTIMIZELY_DATAFILE_URL')}
        >
          <InitializeABTesting>
            <LanguageConfig locale={locale} />
            <CacheProvider value={evaluateInRuntime(isRtl)}>
              <ConfigureI18n locale={locale} countryCode={userCountry}>
                <DeviceLayoutProvider>
                  <GetTheme>
                    {theme => (
                      <ThemeProvider theme={theme}>
                        <InitializeSentry />
                        <CssBaseline />
                        <GridGlobalStyles />
                        <NetworkMonitor />
                        <FontsLoader languageCode={locale} />
                        <UnknownErrorBoundary>
                          <InjectMetaDescription brandName="FindHotel" />
                          <SyncStateWithEnabledBrands
                            enabledBrands={['findhotel']}
                          />
                          <Router history={history}>
                            <Routes />
                            <ErrorOverlayMonitor />
                            <PasswordlessLogin
                              brand={brand}
                              isRtl={isRtl}
                              languageCode={locale}
                              userCountryCode={userCountry}
                              anonymousId={anonymousId}
                              reCaptchaKey={reCaptchaKey}
                            />
                            <CookieProCustomBanner />
                            <FetchUser />
                          </Router>
                        </UnknownErrorBoundary>
                      </ThemeProvider>
                    )}
                  </GetTheme>
                </DeviceLayoutProvider>
              </ConfigureI18n>
            </CacheProvider>
          </InitializeABTesting>
        </OpticksProvider>
      </Suspense>
    </Provider>
  )
}

export default App
